import "./GanttTaskRow.css";

import type { MouseEvent } from "react";
import { JSX } from "react/jsx-runtime";

import { TASK_ROW_HEIGHT } from "../../constants";
import { GanttTask, GanttTaskRowProps } from "../../models";
import { dateToPixel, getDeadlineBarColors, getTaskStatusColor, resolveTaskStatus } from "../../utils";
import { GanttBar } from "../GanttBar";
import { GanttTodayLine } from "../GanttTodayLine";

const StatusDot = ({ status }: { status: string }): JSX.Element => {
  const { dot } = getTaskStatusColor(status);
  return <span className="GanttStatusDot" style={{ backgroundColor: dot }} />;
};

const StagePill = ({ label }: { label: string }): JSX.Element => <span className="GanttStagePill body4">{label}</span>;

const AssigneeAvatars = ({ assignees }: { assignees: GanttTask["assignees"] }): JSX.Element | null => {
  if (!assignees?.length) return null;
  const visible = assignees.slice(0, 3);
  const overflow = assignees.length - visible.length;
  return (
    <div className="GanttAvatarGroup">
      {visible.map((a) => (
        <span
          key={a.uuid}
          className="GanttAvatar body4"
          style={{ backgroundColor: a.color ?? "var(--color-aqua-500)" }}
          title={a.name ?? a.initials}
        >
          {a.initials}
        </span>
      ))}
      {overflow > 0 && <span className="GanttAvatar GanttAvatarOverflow body4">+{overflow}</span>}
    </div>
  );
};

export const GanttTaskRow = ({
  task,
  groupId,
  viewStart,
  pixelsPerDay,
  timelineWidth,
  todayX,
  labelWidth,
  objectMeta,
  onClick,
  onShowTooltip,
  onHideTooltip,
}: GanttTaskRowProps): JSX.Element => {
  const ppd = pixelsPerDay;
  const status = resolveTaskStatus(task.status);
  const { bg: barBg, border: barBorder } = getDeadlineBarColors(task.deadlineDate, status);
  const { dot: statusDotColor, text: statusText } = getTaskStatusColor(status);

  const handleClick = (): void => {
    onClick?.(task.id, groupId);
  };

  const handleBarEnter = (e: MouseEvent): void => {
    const meta = [{ label: "Status", value: status }];
    if (task.deadlineDate) meta.push({ label: "Deadline", value: task.deadlineDate.toLocaleDateString() });
    if (task.stageName) meta.push({ label: "Stage", value: task.stageName });
    // After the stage: a phase is the wider bracket the step sits in, so it reads as the coarser of
    // the two. Only workflow tasks have either.
    if (task.phase) meta.push({ label: "Phase", value: task.phase });
    onShowTooltip?.({ title: task.name, accentColor: statusDotColor, meta }, e);
  };

  return (
    <div className="GanttTaskRow" style={{ minHeight: TASK_ROW_HEIGHT }}>
      {/* ── Left label cell ─────────────────────────────────────────────── */}
      <button type="button" className="GanttTaskRowLabel" style={{ width: labelWidth }} onClick={handleClick}>
        {/* Row 1: name + avatar */}
        <div className="GanttTaskRowTop">
          <span className="GanttTaskName body3" title={task.name}>
            {task.name}
          </span>
          <AssigneeAvatars assignees={task.assignees} />
        </div>
        {/* Row 2: status | (agenda) project·deal  OR  (timeline) workflow stage */}
        <div className="GanttTaskMeta">
          <span className="GanttStatusText body4" style={{ color: statusText }}>
            <StatusDot status={status} />
            {status}
          </span>
          {objectMeta ? (
            <>
              <span className="GanttMetaSep" aria-hidden="true">
                |
              </span>
              <span className="GanttTaskObject body4" title={objectMeta.label}>
                <span className="GanttTaskObjectName">{objectMeta.label}</span>
                {objectMeta.type && <span className="GanttTaskObjectBadge">{objectMeta.type}</span>}
              </span>
            </>
          ) : (
            task.stageName && (
              <>
                <span className="GanttMetaSep" aria-hidden="true">
                  |
                </span>
                <StagePill label={task.stageName} />
              </>
            )
          )}
        </div>
      </button>

      {/* ── Timeline cell ────────────────────────────────────────────────── */}
      <div className="GanttTaskRowTimeline" style={{ width: timelineWidth }}>
        <GanttTodayLine x={todayX} />
        {task.deadlineDate && (
          <GanttBar
            variant={task.isMilestone ? "milestone" : "task-bar"}
            x={dateToPixel(task.deadlineDate, viewStart, ppd)}
            rowHeight={TASK_ROW_HEIGHT}
            color={barBorder}
            bgColor={barBg}
            title={`${task.name}${task.deadlineDate ? ` — ${task.deadlineDate.toLocaleDateString()}` : ""}`}
            onClick={handleClick}
            onMouseEnter={handleBarEnter}
            onMouseLeave={onHideTooltip}
          />
        )}
      </div>
    </div>
  );
};
